import {
  addCourseToCurriculum as apiAddCourseToCurriculum,
  removeCourseFromCurriculum as apiRemoveCourseFromCurriculum,
  updateCurriculum as apiUpdateCurriculum,
  createCurriculum,
  deleteCurriculum,
  moveCourseInCurriculum,
} from '@lms/api-client/backend';
import type { CurriculumNames } from './actions.schema';

// Ports app/[brand]/admin/curriculums/_components/actions.ts. Each Next server
// action becomes a plain async function that calls the backend and returns a
// result the caller acts on. `revalidatePath` is dropped; callers revalidate
// their SWR cache with `mutate` after a successful call.

export type MutationResult = { ok: true } | { ok: false; error: string };

// Ports `addCurriculum`. The Next action read the current site; here the caller
// passes `locationId` (the brand's site id).
export async function addCurriculum(
  names: CurriculumNames,
  locationId: number,
): Promise<MutationResult> {
  try {
    await createCurriculum({
      name: names.en,
      locationId,
      nameTranslated: names,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `updateCurriculum`.
export async function updateCurriculum(
  curriculumId: number,
  names: CurriculumNames,
): Promise<MutationResult> {
  try {
    await apiUpdateCurriculum(curriculumId, {
      name: names.en,
      nameTranslated: names,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `removeCurriculum`.
export async function removeCurriculum(
  curriculumId: number,
): Promise<MutationResult> {
  try {
    await deleteCurriculum(curriculumId);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `addCourseToCurriculum`.
export async function addCourseToCurriculum(
  curriculumId: number,
  courseId: number,
  order: number,
): Promise<MutationResult> {
  try {
    await apiAddCourseToCurriculum(curriculumId, courseId, order);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `removeCourseFromCurriculum`.
export async function removeCourseFromCurriculum(
  curriculumId: number,
  courseId: number,
): Promise<MutationResult> {
  try {
    await apiRemoveCourseFromCurriculum(curriculumId, courseId);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}

// Ports `updateCourseOrder`. `direction` is a relative move (-1 up/left, +1
// down/right); the Next action bailed out on anything outside that range, so we
// keep the same guard before hitting the backend's atomic swap.
export async function updateCourseOrder(
  curriculumId: number,
  courseId: number,
  direction: number,
): Promise<MutationResult> {
  if (direction < -1 || direction > 1) {
    return { ok: true };
  }
  try {
    await moveCourseInCurriculum(curriculumId, courseId, direction);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: `${e}` };
  }
}
